import { Link, useNavigate } from "react-router";

import { AmbientBackdrop } from "@/components/layout/AmbientBackdrop";
import { TopBar } from "@/components/layout/TopBar";
import { Button } from "@/components/ui/Button";
import { ArrowRightIcon, CoinIcon, HomeIcon, LogOutIcon } from "@/components/ui/icons";
import { useLogout, useSession } from "@/features/auth/useSession";
import { useCharacter } from "@/features/character/useCharacter";
import { formatCoal, passportNumber } from "@/utils/format";

/**
 * Signed-in landing page for players who have a character.
 * "Enter Coal City" opens the 3D world (/play).
 */
export function HomePage() {
  const { data: user } = useSession();
  const { data: character } = useCharacter();
  const logout = useLogout();
  const navigate = useNavigate();

  // RequireAuth + RequireCharacter guarantee both; this only satisfies the type checker.
  if (!user || !character) return null;

  const home = character.home;

  const handleLogout = () => {
    logout.mutate(undefined, {
      onSuccess: () => navigate("/login", { replace: true }),
    });
  };

  return (
    <div className="relative flex min-h-full flex-col overflow-hidden">
      <AmbientBackdrop />
      <TopBar>
        <span className="flex items-center gap-2 rounded-full border border-secondary/30 bg-surface-container-high px-3 py-1.5 text-currency-stat text-secondary">
          <CoinIcon className="size-5" />
          {formatCoal(character.wallet.balance)}
        </span>
        <span className="hidden items-center gap-2 rounded-full bg-surface-container-high px-3 py-1.5 text-label-md sm:flex">
          <span className="size-2 rounded-full bg-primary-container shadow-glow-primary" aria-hidden />
          <span>@{user.username}</span>
        </span>
        <Button
          variant="ghost"
          className="h-10"
          onClick={handleLogout}
          loading={logout.isPending}
          icon={<LogOutIcon className="size-4" />}
        >
          Log out
        </Button>
      </TopBar>

      <main className="relative z-10 flex flex-1 items-center justify-center px-4 pb-12 pt-4">
        <section className="glass-panel relative w-full max-w-xl p-6 sm:p-10">
          <div className="absolute inset-x-10 top-0 h-px bg-gradient-to-r from-transparent via-primary-container to-transparent" />

          <p className="text-label-md uppercase text-primary">Welcome back</p>
          <h1 className="mt-2 text-headline-xl font-extrabold tracking-tight">{character.name}</h1>
          <p className="mt-1 text-body-md text-on-surface-variant">
            Passport {passportNumber(character.uid)} · {home?.zone.name ?? "Coal City"}
          </p>

          {logout.isError ? (
            <p role="alert" className="mt-4 text-body-md text-error">
              Couldn't log out — check your connection and try again.
            </p>
          ) : null}

          <ul className="mt-8 space-y-3">
            <li className="flex items-center justify-between gap-4 rounded-xl bg-surface-container-low px-4 py-3.5">
              <span className="flex items-center gap-3">
                <CoinIcon className="size-5 text-secondary" />
                <span>
                  <span className="block text-body-md">Wallet</span>
                  <span className="block text-body-sm text-on-surface-variant">Coal Cash balance</span>
                </span>
              </span>
              <span className="text-currency-stat text-secondary">{formatCoal(character.wallet.balance)}</span>
            </li>

            <li className="flex items-center justify-between gap-4 rounded-xl bg-surface-container-low px-4 py-3.5">
              <span className="flex items-center gap-3">
                <HomeIcon className="size-5 text-primary" />
                <span>
                  <span className="block text-body-md">{home ? home.tier.name : "No home"}</span>
                  <span className="block text-body-sm text-on-surface-variant">
                    {home ? `Plot ${home.plot_code.toUpperCase()} · Tier ${home.tier.level}` : "—"}
                  </span>
                </span>
              </span>
              {home ? <span className="text-label-md uppercase text-primary">Owned</span> : null}
            </li>

          </ul>

          <Button
            className="mt-8 h-14 w-full text-headline-sm"
            onClick={() => navigate("/play")}
            icon={<ArrowRightIcon />}
          >
            Enter Coal City
          </Button>

          <p className="mt-6 text-center text-body-sm text-on-surface-variant">
            <Link to="/system" className="underline-offset-4 hover:text-on-surface hover:underline">
              System status
            </Link>
          </p>
        </section>
      </main>
    </div>
  );
}